import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PRESET_COLORS = [
  "#6B7280", "#3B82F6", "#06B6D4", "#8B5CF6", "#A855F7",
  "#EC4899", "#F59E0B", "#F97316", "#22C55E", "#10B981",
  "#14B8A6", "#84CC16", "#EF4444", "#DC2626",
];

export interface DealStageFormData {
  name: string;
  color: string;
}

interface DealStageFormDialogProps {
  open: boolean;
  title: string;
  description: string;
  initialValues: DealStageFormData;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: DealStageFormData) => void;
}

const DealStageFormDialog: React.FC<DealStageFormDialogProps> = ({
  open,
  title,
  description,
  initialValues,
  saving,
  onOpenChange,
  onSave,
}) => {
  const [name, setName] = useState(initialValues.name);
  const [color, setColor] = useState(initialValues.color);

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({ name: name.trim(), color });
  };

  useEffect(() => {
    if (open) {
      setName(initialValues.name);
      setColor(initialValues.color);
    }
  }, [open, initialValues.name, initialValues.color]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deal-stage-name">Name</Label>
              <Input
                id="deal-stage-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Seller Walkthrough"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((presetColor) => (
                  <button
                    key={presetColor}
                    type="button"
                    onClick={() => setColor(presetColor)}
                    title={presetColor}
                    className="h-7 w-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: presetColor,
                      borderColor: color === presetColor ? "white" : "transparent",
                      boxShadow: color === presetColor ? `0 0 0 2px ${presetColor}` : "none",
                    }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="deal-stage-custom-color" className="text-xs text-muted-foreground">
                  Custom
                </Label>
                <Input
                  id="deal-stage-custom-color"
                  type="color"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                  className="h-7 w-10 cursor-pointer border-0 p-0"
                />
                <span className="font-mono text-xs text-muted-foreground">{color}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DealStageFormDialog;
